import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
import { useToast } from "@/components/ui/use-toast";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";
// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";

// Services
import { getDebt, lastgetDebt } from "@/services/finance";

// modal
import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/finance-debt/"
)({
  component: FinanceDebt,
});

function FinanceDebt() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId"); // Get companyId from local storage
  const yearly = Number(localStorage.getItem("year")); // Get year from local storage
  const { company_financial_report } = useAccessPermission();
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Redirect if no year in localStorage
  React.useEffect(() => {
    if (!yearly) {
      navigate({ to: "/report/finance/company/finance-debt/add" });
    }
  }, [yearly, navigate]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["debt", companyId],
    queryFn: () => getDebt(companyId as string, yearly),
  });
  const {
    data: lastdebt,
    isLoading: lastdebtLoading,
  } = useQuery({
    queryKey: ["lastdebt", companyId, yearly],
    queryFn: () => lastgetDebt(companyId as string, yearly - 1),
    enabled: !!companyId && !!yearly,
  });

  const dayFromNow = calculateDaysFromNow(data?.data?.createdAt);

  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({ to: "/report/finance/company/finance-debt/add" });
    }
  }, [data, navigate, isLoading]);
  const [unit, setUnit] = React.useState("1"); // Move this hook to the top

  if (isError) return <div>Error: {error.message}</div>;

  const formatDisplayNumber = (value: number) => {
    if (!value && value !== 0) return "-";

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formattedValue = (absValue / Number(unit)).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return isNegative ? `(${formattedValue})` : formattedValue;
  };

  const sumAmount = (debts: any[]) =>
    debts?.reduce((total, debt) => total + debt.amount, 0);

  const debtTypes = ["REVOLVING", "NON_REVOLVING", "EQUITY"];
  const debtsByType = debtTypes.reduce((acc: Record<string, any[]>, type) => {
    acc[type] =
      data?.data?.debtDetails?.filter((debt: any) => debt.debtType === type) || [];
    return acc;
  }, {});

  const totalAmounts = debtTypes?.reduce((acc: Record<string, number>, type) => {
    acc[type] = sumAmount(debtsByType[type]);
    return acc;
  }, {});

  const totalAmount = Object?.values(totalAmounts).reduce(
    (total, amount) => total + amount,
    0
  );
  // Last year

  const lastSumAmount = (debts: any[]) =>
    debts?.reduce((total, debt) => total + debt.amount, 0);

  const lastDebtsByType = debtTypes.reduce((acc: Record<string, any[]>, type) => {
    acc[type] =
      lastdebt?.data?.debtDetails?.filter((debt: any) => debt.debtType === type) ||
      [];
    return acc;
  }, {});

  const lastTotalAmounts = debtTypes?.reduce((acc: Record<string, number>, type) => {
    acc[type] = lastSumAmount(lastDebtsByType[type]);
    return acc;
  }, {});

  const lastTotalAmount = Object?.values(lastTotalAmounts).reduce(
    (total, amount) => total + amount,
    0
  );

  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["debt", companyId],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!data?.data?.id) return;
    requireEditMutation.mutate({
      editId: data?.data?.id,
      editRequestType: "DEBT_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: {
                  page: 1,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex items-center gap-[4px]  mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {yearly}</CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {dayFromNow <= 3 ? company_financial_report.isUpdate && (
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/finance-debt/edit",
                    replace: true,
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md  p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>
            ) :
              company_financial_report.isUpdate && (
                data?.data?.editRequestStatus === "REQUEST" ? (
                  <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
                ) : (
                  <Button
                    onClick={() => setOpen(true)}
                    variant="secondary"
                    className="flex items-center border border-blue-500 gap-x-1 w-[120px] p-2 text-blue-500"
                  >
                    <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                  </Button>
                ))}
          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
            isLoading={requireEditMutation.isPending}
          />
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <div>
            {isLoading || lastdebtLoading ? (
              <DinamixSkeletonTable />
            ) : (
              <Table className="mb-4">
                <TableHeader>
                  <TableRow className="[&>*]:whitespace-nowrap [&>*]:border [&>*]:font-bold [&>*]:text-black">
                    <TableHead className="border">ໜີ້ສິນ ແລະ ທຶນ</TableHead>
                    <TableHead className="border">
                      ມູນຄ່າຍັງເຫຼືອ(ປີນີ້) {yearly}
                    </TableHead>
                    <TableHead className="border">
                      ມູນຄ່າຍັງເຫຼືອ(ປີຜ່ານມາ) {yearly - 1}
                    </TableHead>

                    <TableHead className="border">ໝາຍເຫດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debtTypes.map((type, index) => (
                    <React.Fragment key={index}>
                      <TableRow className="font-bold cursor-pointer bg-[#216C4C]/20">
                        <TableCell className="border" colSpan={4}>
                          {type === "REVOLVING"
                            ? "ໜີ້ສິນໝູນວຽນ"
                            : type === "NON_REVOLVING"
                              ? "ໜີ້ສິນບໍ່ໝູນວຽນ"
                              : "ທຶນ"}
                        </TableCell>
                      </TableRow>
                      {debtsByType[type]?.map((debt, index) => (
                        <TableRow key={index} className="cursor-pointer">
                          <TableCell className="border">{debt?.name}</TableCell>
                          <TableCell className="text-right border">
                            {formatDisplayNumber(debt?.amount)}
                          </TableCell>
                          {/* year */}
                          <TableCell className="text-right border">
                            {lastDebtsByType[type]?.find(
                              (lastDebt) => lastDebt.name === debt.name
                            )?.amount
                              ? formatDisplayNumber(
                                lastDebtsByType[type].find(
                                  (lastDebt) => lastDebt.name === debt.name
                                )!.amount
                              )
                              : "-"}
                          </TableCell>
                          <TableCell className="border">
                            {debt?.remark}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-100 [&>*]:text-black [&>*]:font-bold">
                        <TableCell className="border">
                          {type === "REVOLVING"
                            ? "ລວມຍອດ ໜີ້ສິນໝູນວຽນ ( I )"
                            : type === "NON_REVOLVING"
                              ? "ລວມຍອດ ໜີ້ສິນບໍ່ໝູນວຽນ ( II )"
                              : "ລວມຍອດທຶນຕົນເອງ ( III )"}
                        </TableCell>
                        <TableCell className="text-right border">
                          {formatDisplayNumber(totalAmounts[type])}
                        </TableCell>
                        <TableCell className="text-right border">
                          {formatDisplayNumber(lastTotalAmounts[type]) ||
                            "-"}
                        </TableCell>
                        <TableCell className="border"> </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="border" colSpan={3}>
                          {" "}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  <TableRow className="bg-gray-100 [&>*]:text-black [&>*]:font-bold">
                    <TableCell className="border">
                      ລວມຍອດໜີ້ສິນທັງໝົດ ( I + II + III )
                    </TableCell>
                    <TableCell className="text-right border">
                      {formatDisplayNumber(totalAmount)}
                    </TableCell>

                    <TableCell className="text-right border">
                      {formatDisplayNumber(lastTotalAmount) || "-"}
                    </TableCell>
                    <TableCell className="border">-</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
          {data?.data?.docFile
            ? company_financial_report.isDownload && (
              <FileHandleViewer fileUrl={data?.data?.docFile} />
            )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
